import { useState, useEffect, useRef, lazy, Suspense, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  DrawerContent,
} from '@/components/ui/drawer';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { X, Loader2, CreditCard } from 'lucide-react';
import { useCurrencyDetection } from '@/hooks/use-currency-detection';
import { useAppConfig } from '@/hooks/use-app-config';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/components/AuthProvider';
import { useIOSDetection } from '@/hooks/use-ios-detection';
import { useAndroidDetection } from '@/hooks/use-android-detection';
import { useToast } from '@/hooks/use-toast';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { supabase } from '@/integrations/supabase/client';
import { MediaUnlocker } from '@/lib/mediaUnlocker';
import { usePaymentRecoveryPolling } from '@/hooks/use-payment-recovery-polling';
import { useQueryClient } from '@tanstack/react-query';
import { useReadyPaymentRequest } from '@/hooks/use-ready-payment-request';
import { getCachedPublishableKey } from '@/lib/stripePreloader';

// Lazy load heavy payment components to reduce initial bundle
const StripeElementsPaymentForm = lazy(() => 
  import('@/components/subscription/StripeElementsPaymentForm').then(m => ({ default: m.StripeElementsPaymentForm }))
);
const PaymentSuccessAnimation = lazy(() => 
  import('@/components/PaymentSuccessAnimation').then(m => ({ default: m.PaymentSuccessAnimation }))
);

interface SubscriptionTierSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTier?: (tier: 'monthly' | 'annual', price: number) => void;
  onSuccess?: () => void;
  defaultTier?: 'monthly' | 'annual';
}

export function SubscriptionTierSelector({ 
  isOpen, 
  onClose, 
  onSelectTier,
  onSuccess,
  defaultTier = 'annual'
}: SubscriptionTierSelectorProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: subscriptionStatus } = useSubscriptionStatus();
  const [selectedTier, setSelectedTier] = useState<'monthly' | 'annual'>(defaultTier);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const { isIOS } = useIOSDetection();
  const isAndroid = useAndroidDetection();
  const [showSuccess, setShowSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Track if Apple Pay sheet is open for recovery polling
  const [applePayActive, setApplePayActive] = useState(false);
  const { currency, country } = useCurrencyDetection();
  const { data: appConfig } = useAppConfig();
  const isMobile = useIsMobile();
  
  // Use pre-warmed Stripe for instant Apple Pay / Google Pay
  const {
    paymentRequest,
    canMakePayment,
    isReady: paymentMethodsReady,
    stripe: stripeInstance,
    publishableKey: stripePublishableKey,
    Elements: StripeElements,
    PaymentRequestButtonElement: PaymentRequestButton,
    updateAmount,
  } = useReadyPaymentRequest({ defaultTier, enabled: isOpen });
  
  // Track if handler is attached to prevent double-attachment
  const handlerAttachedRef = useRef(false);
  // CRITICAL: Synchronous tier ref - always current, no async delay
  const selectedTierRef = useRef<'monthly' | 'annual'>(defaultTier);
  
  // Pre-fetched SetupIntent client secret for instant card form loading
  const [prefetchedClientSecret, setPrefetchedClientSecret] = useState<string | null>(null);
  const setupIntentFetchedRef = useRef(false);

  // Recovery polling - activates when Apple Pay is active
  const handleRecoverySuccess = useCallback(() => {
    console.log('[TierSelector] Recovery polling detected subscription!');
    setApplePayActive(false);
    setShowSuccess(true);
    queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
    
    if (onSuccess) {
      onSuccess();
    } else {
      onClose();
      setTimeout(() => {
        navigate('/?payment_success=true', { replace: true });
      }, 1500);
    }
  }, [navigate, queryClient, onSuccess, onClose]);

  usePaymentRecoveryPolling({
    enabled: applePayActive,
    onSuccess: handleRecoverySuccess,
    pollInterval: 2000,
    maxDuration: 30000,
  });

  // Use stable price values - don't change after initial render
  const monthlyPrice = 7.99;
  const annualPrice = appConfig?.annual_price || 47.88;
  const annualMonthlyDisplay = (annualPrice / 12).toFixed(2);
  const savings = monthlyPrice * 12 - annualPrice;

  // Synchronous tier change handler - updates ref immediately before state
  const handleTierChange = (tier: 'monthly' | 'annual') => {
    console.log('[TierSelector] Tier changed to:', tier);
    selectedTierRef.current = tier; // Synchronous update - always current
    setSelectedTier(tier);
    updateAmount(tier); // Update PaymentRequest amount instantly
  };

  // Pre-fetch SetupIntent when modal opens for instant card form loading
  useEffect(() => {
    if (!isOpen || !user?.email) return;
    if (setupIntentFetchedRef.current) return;
    
    setupIntentFetchedRef.current = true;
    console.log('[TierSelector] Pre-fetching SetupIntent...');
    
    supabase.functions.invoke('create-setup-intent', {
      body: { email: user.email }
    }).then(({ data, error }) => {
      if (error || !data?.client_secret) {
        console.error('[TierSelector] Pre-fetch SetupIntent failed:', error);
        return;
      }
      console.log('[TierSelector] SetupIntent pre-fetched successfully');
      setPrefetchedClientSecret(data.client_secret);
    });
  }, [isOpen, user?.email]);

  // Keep selectedTierRef in sync
  useEffect(() => {
    selectedTierRef.current = selectedTier;
  }, [selectedTier]);

  // Ref to hold latest values for stable handler
  const handlerRef = useRef<{
    user: typeof user;
    stripeInstance: any;
    selectedTier: 'monthly' | 'annual';
    currency: typeof currency;
    navigate: typeof navigate;
    toast: typeof toast;
    onClose: typeof onClose;
    onSuccess: typeof onSuccess;
    setIsProcessing: typeof setIsProcessing;
    setShowSuccess: typeof setShowSuccess;
  }>({
    user,
    stripeInstance,
    selectedTier,
    currency,
    navigate,
    toast,
    onClose,
    onSuccess,
    setIsProcessing,
    setShowSuccess,
  });

  // Update ref when values change
  useEffect(() => {
    handlerRef.current = {
      user,
      stripeInstance,
      selectedTier,
      currency,
      navigate,
      toast,
      onClose,
      onSuccess,
      setIsProcessing,
      setShowSuccess,
    };
  }, [user, stripeInstance, selectedTier, currency, navigate, toast, onClose, onSuccess]);

  // Handle payment method events - attach ONCE when paymentRequest is available
  // Use handlerAttachedRef to prevent double-attachment
  useEffect(() => {
    if (!paymentRequest) {
      console.log('[TierSelector] No paymentRequest yet, skipping handler attachment');
      return;
    }
    
    if (handlerAttachedRef.current) {
      console.log('[TierSelector] Handler already attached, skipping');
      return;
    }

    console.log('[TierSelector] Attaching paymentmethod handler NOW');
    handlerAttachedRef.current = true;

    const handlePaymentMethod = async (ev: any) => {
      console.log('[TierSelector] *** PAYMENTMETHOD EVENT FIRED ***');
      console.log('[TierSelector] Event details:', ev.paymentMethod?.type, ev.paymentMethod?.id);
      
      // CRITICAL: Read tier from synchronous ref, NOT from handlerRef which may be stale
      const tierToUse = selectedTierRef.current;
      const { user, stripeInstance, currency, navigate, toast, onClose, onSuccess, setIsProcessing, setShowSuccess } = handlerRef.current;
      
      console.log('[TierSelector] CRITICAL - Using tier from ref:', tierToUse);
      console.log('[TierSelector] Handler context - user:', !!user, 'stripe:', !!stripeInstance);
      
      if (!user || !stripeInstance) {
        console.error('[TierSelector] Missing user or stripe instance');
        try { ev.complete('fail'); } catch (e) { console.error('[TierSelector] ev.complete fail error:', e); }
        return;
      }

      setIsProcessing(true);
      // Start recovery polling as fallback
      setApplePayActive(true);
      
      try {
        MediaUnlocker.instance().unlock().catch(() => {});

        console.log('[TierSelector] Creating setup intent...');
        const { data, error } = await supabase.functions.invoke('create-setup-intent', {
          body: { email: user.email }
        });
        
        if (error || !data?.client_secret) {
          console.error('[TierSelector] Setup intent error:', error);
          try { ev.complete('fail'); } catch (e) { console.error('[TierSelector] ev.complete fail error:', e); }
          throw new Error('Failed to create payment setup');
        }

        console.log('[TierSelector] Confirming card setup with payment method:', ev.paymentMethod.id);
        const { error: confirmError } = await stripeInstance.confirmCardSetup(
          data.client_secret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        );

        if (confirmError) {
          console.error('[TierSelector] Card setup error:', confirmError);
          try { ev.complete('fail'); } catch (e) { console.error('[TierSelector] ev.complete fail error:', e); }
          throw confirmError;
        }

        console.log('[TierSelector] CRITICAL: Starting subscription with tier:', tierToUse);
        const { data: subData, error: subError } = await supabase.functions.invoke('start-trial-subscription', {
          body: {
            payment_method: ev.paymentMethod.id,
            tier: tierToUse,  // Use the synchronous ref value
            currency: currency.code.toLowerCase()
          }
        });
        
        // CRITICAL: Verify BOTH no error AND subscription was actually created
        if (subError || !subData?.subscription_id) {
          console.error('[TierSelector] Subscription error:', subError, subData);
          try { ev.complete('fail'); } catch (e) { console.error('[TierSelector] ev.complete fail error:', e); }
          throw new Error(subData?.error || subError?.message || 'Subscription creation failed');
        }
        
        console.log('[TierSelector] Subscription created successfully:', subData.subscription_id);

        console.log('[TierSelector] Payment successful!');
        
        // Complete payment sheet - wrapped in try-catch to prevent blocking
        try {
          ev.complete('success');
        } catch (completeError) {
          console.error('[TierSelector] ev.complete success error:', completeError);
        }
        
        // Stop recovery polling since we succeeded
        setApplePayActive(false);
        
        // Invalidate subscription queries
        queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
        
        setShowSuccess(true);
        
        // If parent provided onSuccess, let parent handle navigation
        if (onSuccess) {
          console.log('[TierSelector] Calling onSuccess callback');
          try {
            onSuccess();
          } catch (successError) {
            console.error('[TierSelector] onSuccess callback error:', successError);
            window.location.href = '/?payment_success=true';
          }
        } else {
          // Fallback: handle navigation internally
          console.log('[TierSelector] No onSuccess callback, handling navigation internally');
          onClose();
          try {
            setTimeout(() => {
              navigate('/?payment_success=true', { replace: true });
            }, 2000);
          } catch (navError) {
            console.error('[TierSelector] Navigation error:', navError);
            window.location.href = '/?payment_success=true';
          }
        }
      } catch (error: any) {
        console.error('[TierSelector] Payment error:', error);
        // Don't stop polling on error - let it continue to catch Stripe-side success
        try { ev.complete('fail'); } catch (e) { console.error('[TierSelector] ev.complete fail error:', e); }
        toast({
          title: "Payment Failed",
          description: error.message || "Unable to complete payment. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsProcessing(false);
      }
    };

    // Listen for cancel event to stop polling if user dismisses
    const handleCancel = () => {
      console.log('[TierSelector] Payment cancelled by user');
      setApplePayActive(false);
    };

    paymentRequest.on('paymentmethod', handlePaymentMethod);
    paymentRequest.on('cancel', handleCancel);

    return () => {
      console.log('[TierSelector] Cleanup - removing paymentmethod handler');
      handlerAttachedRef.current = false;
      paymentRequest.off('paymentmethod', handlePaymentMethod);
      paymentRequest.off('cancel', handleCancel);
    };
  }, [paymentRequest, queryClient]); // Added queryClient

  
  const handleBackFromPayment = () => {
    setShowPaymentForm(false);
  };

  // Disable body scroll and make background inert when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const mainContent = document.getElementById('main-content');
      if (mainContent) {
        mainContent.setAttribute('inert', '');
        mainContent.style.pointerEvents = 'none';
      }
    } else {
      document.body.style.overflow = '';
      const mainContent = document.getElementById('main-content');
      if (mainContent) {
        mainContent.removeAttribute('inert');
        mainContent.style.pointerEvents = '';
      }
    }
    return () => {
      document.body.style.overflow = '';
      const mainContent = document.getElementById('main-content');
      if (mainContent) {
        mainContent.removeAttribute('inert');
        mainContent.style.pointerEvents = '';
      }
    };
  }, [isOpen]);

  const handlePaymentSuccess = () => {
    // Close the sheet/drawer
    onClose();
  };


  const content = showPaymentForm ? (
    <Suspense fallback={<div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-white/60" /></div>}>
      <StripeElementsPaymentForm
        tier={selectedTier}
        price={selectedTier === 'monthly' ? monthlyPrice : annualPrice}
        currencyCode={currency.code}
        currencySymbol={currency.symbol}
        stripePublishableKey={stripePublishableKey}
        onBack={handleBackFromPayment}
        onSuccess={handlePaymentSuccess}
        prefetchedClientSecret={prefetchedClientSecret}
      />
    </Suspense>
  ) : (
    <>
      {showSuccess && (
        <Suspense fallback={null}>
          <PaymentSuccessAnimation />
        </Suspense>
      )}
      <div className="flex flex-col h-full pt-4">
      {/* Header - only show custom close button on mobile */}
      <div className="flex items-center justify-between px-6 pb-4">
        <div className="flex items-center">
          <img src="/logo.png" alt="Logo" className="h-8 w-auto" />
          <span className="text-white font-semibold -ml-0.5" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif', letterSpacing: '-0.02em', fontSize: '28px', lineHeight: '32px' }}>Plan</span>
        </div>
        {isMobile && (
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        )}
      </div>

      <div className="flex-1 flex flex-col px-5 pb-8 space-y-3 overflow-y-auto overscroll-contain">
        {/* Choose Your Plan Section */}
        <div className="bg-white/5 rounded-[24px] p-4 backdrop-blur-xl">
          <h2 className="text-[20px] font-semibold text-white text-center mb-3">Choose your plan</h2>
          
          {/* Toggle with Pricing */}
          <div className="relative bg-black/30 rounded-[20px] p-1 flex items-center">
            {/* Sliding background */}
            <div 
              className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-[#007AFF] rounded-[16px] transition-all duration-300 ${
                selectedTier === 'monthly' ? 'left-[calc(50%+4px)]' : 'left-1'
              }`}
            />
            
            {/* Annual Option */}
            <button
              onClick={() => handleTierChange('annual')}
              className="relative z-10 flex-1 py-2.5 text-center"
            >
              <div className={`font-semibold text-[15px] transition-colors ${
                selectedTier === 'annual' ? 'text-white' : 'text-white/50'
              }`}>
                Annual
              </div>
              <div className={`text-[12px] transition-colors mt-0.5 ${
                selectedTier === 'annual' ? 'text-white/90' : 'text-white/40'
              }`}>
                {currency.symbol}{annualMonthlyDisplay}/month
              </div>
            </button>
            
            {/* Monthly Option */}
            <button
              onClick={() => handleTierChange('monthly')}
              className="relative z-10 flex-1 py-2.5 text-center"
            >
              <div className={`font-semibold text-[15px] transition-colors ${
                selectedTier === 'monthly' ? 'text-white' : 'text-white/50'
              }`}>
                Monthly
              </div>
              <div className={`text-[12px] transition-colors mt-0.5 ${
                selectedTier === 'monthly' ? 'text-white/90' : 'text-white/40'
              }`}>
                {currency.symbol}{monthlyPrice}/month
              </div>
            </button>
          </div>
        </div>

        {/* Pricing Breakdown */}
        <div className="bg-white/5 rounded-[24px] p-4 backdrop-blur-xl space-y-2">
          {selectedTier === 'annual' && (
            <div className="text-center pb-2">
              <span className="text-white text-[15px] font-semibold">
                {currency.symbol}{annualMonthlyDisplay}/month
              </span>
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <span className="text-white/70 text-[13px]">Total</span>
            <div className="text-right">
              <div className="text-white text-[16px] font-semibold">
                {currency.symbol}{selectedTier === 'annual' ? annualPrice.toFixed(2) : monthlyPrice.toFixed(2)} today
              </div>
              <div className="text-white/50 text-[10px] mt-0.5">
                {selectedTier === 'annual' ? 'Billed annually' : 'Billed monthly'}
              </div>
            </div>
          </div>
          
          {selectedTier === 'annual' && savings > 0 && (
            <div className="flex justify-between items-center pt-2 border-t border-white/10">
              <span className="text-white/70 text-[13px]">Savings</span>
              <span className="text-white text-[16px] font-semibold">
                -{currency.symbol}{savings.toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="bg-white/5 rounded-[24px] p-4 backdrop-blur-xl space-y-1.5">
          <div className="text-white/70 text-[13px]">Cancel anytime</div>
          <div className="text-white/70 text-[13px]">All books included</div>
          <div className="text-white/70 text-[13px]">New releases</div>
          <div className="text-white/70 text-[13px]">Up to 5 profiles</div>
        </div>

        {/* Payment Buttons */}
        <div className="space-y-3">
          {/* Loading skeleton while payment button initializes */}
          {!paymentMethodsReady && (
            <div className="w-full h-[52px] bg-white/20 rounded-[4px] animate-pulse flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-white/60" />
            </div>
          )}
          
          {/* Stripe Payment Request Button (Apple Pay / Google Pay) */}
          {paymentMethodsReady && canMakePayment && paymentRequest && stripeInstance && StripeElements && PaymentRequestButton && (
            <StripeElements stripe={stripeInstance}>
              <PaymentRequestButton 
                options={{ 
                  paymentRequest,
                  style: {
                    paymentRequestButton: {
                      type: 'default',
                      theme: 'dark',
                      height: '56px',
                    },
                  },
                }} 
              />
            </StripeElements>
          )}

          {/* Pay by Card Button - always visible */}
          <Button
            onClick={() => {
              MediaUnlocker.instance().unlock().catch(() => {});
              setShowPaymentForm(true);
            }}
            disabled={isProcessing}
            className="w-full h-[52px] text-[15px] font-medium bg-white text-black hover:bg-white/90 rounded-[4px] flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                Pay by Card
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer 
        open={isOpen} 
        onOpenChange={onClose}
        modal={true}
        dismissible={false}
        shouldScaleBackground={false}
      >
        <DrawerContent 
          className="z-[15000] h-auto max-h-[95vh] rounded-[48px] animate-slide-in-bottom overflow-hidden flex flex-col border border-white/30 mx-[3px] mb-[2px]"
          style={{
            background: 'rgba(40, 40, 44, 0.6)',
            backdropFilter: 'blur(60px) saturate(180%)',
            WebkitBackdropFilter: 'blur(60px) saturate(180%)',
            boxShadow: 'inset 0 1px 0 0 rgba(255,255,255,0.15), 0 -10px 40px rgba(0,0,0,0.3)',
          }}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right"
        className="z-[15000] w-[420px] p-0 border-l border-white/30 rounded-l-[48px] mr-[1px]"
        style={{
          background: 'rgba(40, 40, 44, 0.6)',
          backdropFilter: 'blur(60px) saturate(180%)',
          WebkitBackdropFilter: 'blur(60px) saturate(180%)',
          boxShadow: 'inset 1px 0 0 0 rgba(255,255,255,0.15), -10px 0 40px rgba(0,0,0,0.3)',
        }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {content}
      </SheetContent>
    </Sheet>
  );
}
